import { JobState } from './types';

interface Props {
  jobState: JobState;
}

function formatTime(seconds: number): string {
  if (seconds <= 0) return 'Done';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatSize(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
}

export default function ProgressStrip({ jobState }: Props) {
  const { status, progress, filesCount, filesOptimized, savedSize, estimatedTimeRemaining } = jobState;

  const isRunning  = status === 'running' || status === 'starting';
  const isComplete = status === 'success';
  const isFailed   = status === 'failed' || status === 'cancelled';

  const pillColor  = isComplete ? '#00ffd1' : isFailed ? '#f87171' : '#00ff66';
  const pillBg     = isComplete ? 'rgba(0,255,209,0.07)' : isFailed ? 'rgba(248,113,113,0.07)' : 'rgba(0,255,102,0.07)';
  const pillBorder = isComplete ? 'rgba(0,255,209,0.2)' : isFailed ? 'rgba(248,113,113,0.2)' : 'rgba(0,255,102,0.2)';
  const pillLabel  = isComplete ? 'Complete' : isFailed ? (status === 'cancelled' ? 'Cancelled' : 'Failed') : 'Running';

  const barColor = isComplete
    ? 'linear-gradient(90deg,#00ff66,#00ffd1)'
    : isFailed
    ? '#f87171'
    : 'linear-gradient(90deg,#00ffd1,#a78bfa)';

  return (
    <div
      className="flex items-center gap-4 px-6 py-3 border-b border-white/[0.04] shrink-0"
      style={{ background: isComplete ? 'rgba(0,255,102,0.02)' : '#0a0d16' }}
    >
      <div
        className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-full border shrink-0"
        style={{ color: pillColor, background: pillBg, borderColor: pillBorder }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: pillColor }} />
        {pillLabel}
      </div>

      <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${progress}%`, background: barColor, boxShadow: isRunning ? '0 0 10px rgba(0,255,209,0.3)' : 'none' }}
        />
      </div>

      <span className="font-mono text-sm font-black shrink-0" style={{ color: isComplete ? '#00ff66' : '#00ffd1' }}>
        {progress}%
      </span>

      <div className="flex items-center gap-4 shrink-0">
        <div className="w-px h-6 bg-white/[0.05]" />
        <div className="flex flex-col items-center gap-0">
          <span className="text-xs font-bold text-slate-200">
            {filesOptimized.toLocaleString()}
            <span className="text-[10px] text-slate-500"> / {filesCount.toLocaleString()}</span>
          </span>
          <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-600">Optimized</span>
        </div>
        <div className="w-px h-6 bg-white/[0.05]" />
        <div className="flex flex-col items-center gap-0">
          <span className="text-xs font-bold text-[#00ffd1]">{formatSize(savedSize)}</span>
          <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-600">Saved</span>
        </div>
        <div className="w-px h-6 bg-white/[0.05]" />
        <div className="flex flex-col items-center gap-0">
          <span className="text-xs font-bold text-slate-200">
            {isRunning ? formatTime(estimatedTimeRemaining) : isComplete ? 'Done' : '—'}
          </span>
          <span className="text-[8.5px] font-bold uppercase tracking-wider text-slate-600">Remaining</span>
        </div>
      </div>
    </div>
  );
}
